import { useState, useEffect } from 'react'
import { useStory } from '../contexts/StoryContext'
import Header, { SettingsModal } from './Header'
import './StoryLibrary.css'
import './Header.css'

// Sample curated stories (fallback for offline/errors)
import { SAMPLE_STORIES } from '../data/sampleStories'

// Cache for loaded story data
const storyCache = new Map()

function StoryLibrary({ onSelectStory, onCreateStory, onResetOnboarding }) {
  const { userSettings, savedStories, loadStory } = useStory()
  const [stories, setStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('curated')
  const [loadingStory, setLoadingStory] = useState(null)
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    loadStoriesFromManifest()
  }, [userSettings])

  async function loadStoriesFromManifest() {
    setLoading(true)
    
    try {
      // Fetch manifest from public/stories
      const manifestRes = await fetch('/stories/manifest.json')
      if (!manifestRes.ok) throw new Error('Manifest fetch failed')
      
      const manifest = await manifestRes.json()
      
      // Filter stories by age group and language
      const ageMap = { little: [4,6], middle: [7,9], bigger: [10,12] }
      const [min, max] = ageMap[userSettings.ageGroup] || [4, 12]
      const userLanguage = userSettings.language || 'en'
      
      const filteredStories = manifest.stories.filter(story => {
        const ageMatch = story.ageRange[0] <= max && story.ageRange[1] >= min
        // Filter by language - if story has a language field, it must match user's language
        const langMatch = !story.language || story.language === userLanguage
        return ageMatch && langMatch
      })
      
      // Transform manifest entries to display format
      const displayStories = filteredStories.map(story => ({
        storyId: story.id,
        title: story.title,
        file: story.file,
        ageRange: story.ageRange,
        theme: story.theme,
        coverEmoji: story.coverEmoji,
        description: story.description,
        featured: story.featured,
        // These will be populated when story is loaded
        choices: '?',
        endings: '?'
      }))
      
      setStories(displayStories)
    } catch (error) {
      console.warn('Failed to load manifest, using sample stories:', error)
      // Fallback to sample stories
      const ageMap = { little: [4,6], middle: [7,9], bigger: [10,12] }
      const [min, max] = ageMap[userSettings.ageGroup] || [4, 12]
      const userLanguage = userSettings.language || 'en'
      
      const filteredStories = SAMPLE_STORIES.filter(story => {
        const ageMatch = story.ageRange[0] <= max && story.ageRange[1] >= min
        // Filter by language - derive language from culture field (e.g., 'es-MX' -> 'es', 'en-US' -> 'en')
        // For backward compatibility, stories without culture field are treated as English
        const storyLang = story.culture ? story.culture.split('-')[0] : (story.language || 'en')
        const langMatch = storyLang === userLanguage
        return ageMatch && langMatch
      })
      
      setStories(filteredStories)
    } finally {
      setLoading(false)
    }
  }

  async function handleSelectStory(story) {
    // If story already has nodes (from SAMPLE_STORIES), load directly
    if (story.nodes) {
      loadStory(story)
      onSelectStory()
      return
    }
    
    // If no file specified (source: sample), load from SAMPLE_STORIES
    if (!story.file) {
      const sampleStory = SAMPLE_STORIES.find(s => s.storyId === story.storyId)
      if (sampleStory) {
        loadStory(sampleStory)
        onSelectStory()
        return
      }
    }
    
    // Otherwise, fetch the full story JSON
    setLoadingStory(story.storyId)
    
    try {
      // Check cache first
      if (storyCache.has(story.storyId)) {
        loadStory(storyCache.get(story.storyId))
        onSelectStory()
        return
      }
      
      // Fetch story JSON
      const storyRes = await fetch(`/stories/${story.file}`)
      if (!storyRes.ok) throw new Error(`Failed to load story: ${story.file}`)
      
      const fullStory = await storyRes.json()
      
      // Merge manifest data with full story
      const mergedStory = {
        ...story,
        ...fullStory,
        storyId: story.storyId
      }
      
      // Cache it
      storyCache.set(story.storyId, mergedStory)
      
      loadStory(mergedStory)
      onSelectStory()
    } catch (error) {
      console.error('Failed to load story:', error)
      // Try to find in sample stories as fallback
      const fallbackStory = SAMPLE_STORIES.find(s => s.storyId === story.storyId)
      if (fallbackStory) {
        loadStory(fallbackStory)
        onSelectStory()
      } else {
        alert('Sorry, this story could not be loaded. Please try again.')
      }
    } finally {
      setLoadingStory(null)
    }
  }

  const handleResetOnboarding = () => {
    setShowSettings(false)
    onResetOnboarding()
  }

  return (
    <div className="story-library">
      <Header 
        title="📚 StoryPath"
        showSettings={true}
        onSettings={() => setShowSettings(true)}
      />

      <div className="library-subheader">
        <p className="greeting">
          {userSettings.language === 'es' 
            ? '¿Qué aventura te espera hoy?' 
            : 'What adventure awaits today?'}
        </p>
      </div>

      <nav className="library-tabs">
        <button 
          className={`tab ${activeTab === 'curated' ? 'active' : ''}`}
          onClick={() => setActiveTab('curated')}
        >
          📖 {userSettings.language === 'es' ? 'Historias' : 'Stories'}
        </button>
        {userSettings.ageGroup !== 'little' && (
          <button 
            className={`tab ${activeTab === 'created' ? 'active' : ''}`}
            onClick={() => setActiveTab('created')}
          >
            ✨ {userSettings.language === 'es' ? 'Mis Historias' : 'My Stories'}
          </button>
        )}
      </nav>

      <main className="library-content">
        {loading ? (
          <div className="loading">
            {userSettings.language === 'es' ? 'Cargando historias...' : 'Loading stories...'}
          </div>
        ) : activeTab === 'curated' ? (
          <div className="story-grid">
            {stories.map(story => (
              <button 
                key={story.storyId}
                className={`story-card ${loadingStory === story.storyId ? 'loading' : ''}`}
                onClick={() => handleSelectStory(story)}
                disabled={loadingStory !== null}
              >
                <div className="card-thumb">
                  {story.coverEmoji ? (
                    <span className="cover-emoji">{story.coverEmoji}</span>
                  ) : story.thumbnail ? (
                    <img src={story.thumbnail} alt={story.title} />
                  ) : (
                    <span className="placeholder-thumb">📖</span>
                  )}
                </div>
                <h3 className="card-title">{story.title}</h3>
                <p className="card-description">{story.description}</p>
                {loadingStory === story.storyId && (
                  <div className="card-loading">Loading...</div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="story-grid">
            {savedStories.length === 0 ? (
              <div className="empty-state">
                <p>
                  {userSettings.language === 'es' 
                    ? '¡Todavía no has creado ninguna historia!' 
                    : "You haven't created any stories yet!"}
                </p>
                <button className="create-btn" onClick={onCreateStory}>
                  ✨ {userSettings.language === 'es' ? 'Crea Tu Primera Historia' : 'Create Your First Story'}
                </button>
              </div>
            ) : (
              savedStories.map(story => (
                <button 
                  key={story.storyId}
                  className="story-card created"
                  onClick={() => handleSelectStory(story)}
                >
                  <div className="card-thumb">
                    <span className="placeholder-thumb">✨</span>
                  </div>
                  <h3 className="card-title">{story.title}</h3>
                  <p className="card-meta">
                    {userSettings.language === 'es' ? 'Creado por ti' : 'Created by you'}
                  </p>
                </button>
              ))
            )}
          </div>
        )}
      </main>

      {userSettings.ageGroup !== 'little' && (
        <footer className="library-footer">
          <button className="create-fab" onClick={onCreateStory}>
            ✨ {userSettings.language === 'es' ? 'Crear Historia' : 'Create Story'}
          </button>
        </footer>
      )}

      <SettingsModal 
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        userSettings={userSettings}
        onResetOnboarding={handleResetOnboarding}
      />
    </div>
  )
}

export default StoryLibrary
